import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, ArrowLeft, Sparkles, ShieldCheck } from "lucide-react";
import Seo, { breadcrumbLd, faqLd, SITE_URL } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { getCompetitor, COMPETITORS } from "@/data/seoContent";

function Cell({ value, accent }) {
  if (value === true) return <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600"><Check className="h-4 w-4" strokeWidth={2.5} /> Yes</span>;
  if (value === false) return <span className="inline-flex items-center gap-1.5 text-ink-400"><X className="h-4 w-4" strokeWidth={2} /> No</span>;
  return <span className={accent ? "font-semibold text-ink" : "text-ink-600"}>{value}</span>;
}

export default function ComparePage() {
  const { slug } = useParams();
  const c = getCompetitor(slug);
  if (!c) return <Navigate to="/compare" replace />;

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Compare", path: "/compare" },
      { name: `${c.competitor} alternative`, path: `/compare/${c.slug}` },
    ]),
    faqLd(c.faqs),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: c.metaTitle,
      description: c.metaDescription,
      about: `${c.competitor} alternative`,
      publisher: { "@type": "Organization", name: "Ask Redline", url: SITE_URL },
    },
  ];

  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo title={c.metaTitle} description={c.metaDescription} canonical={`/compare/${c.slug}`} jsonLd={jsonLd} scrollTop />
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-sand-200">
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-16">
          <Link to="/compare" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-redline-600 transition-colors mb-6" data-testid="compare-back">
            <ArrowLeft className="h-4 w-4" /> All comparisons
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> {c.hero.eyebrow}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]" data-testid="compare-title">{c.hero.title}</h1>
            <p className="mt-5 text-lg text-ink-700 leading-relaxed">{c.hero.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/#contact" className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-6 py-3 hover:bg-redline-600 transition-colors">Book a demo <ArrowRight className="h-4 w-4" /></a>
              <Link to="/solutions" className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white font-semibold px-6 py-3 hover:border-ink transition-colors">Explore solutions</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STRENGTHS + GAPS */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-sand-200 bg-white p-8">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400 mb-4">What {c.competitor} is great at</div>
          <ul className="space-y-3">
            {c.strengths.map((s) => (
              <li key={s} className="flex items-start gap-3 text-ink-700"><Check className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} /> {s}</li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-ink-500 leading-relaxed border-t border-sand-100 pt-5">{c.complement}</p>
        </div>
        <div className="rounded-3xl border border-sand-200 bg-white p-8">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-redline-500 mb-4">Where teams add or switch to Ask Redline</div>
          <div className="space-y-5">
            {c.gaps.map((g) => (
              <div key={g.title}>
                <div className="font-heading font-bold text-ink">{g.title}</div>
                <p className="text-sm text-ink-600 mt-1 leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="max-w-2xl mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Side by side</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Ask Redline vs {c.competitor}</h2>
        </div>
        <div className="overflow-x-auto rounded-3xl border border-sand-200 bg-white" data-testid="compare-table">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-sand-200 bg-sand-50">
                <th className="text-left font-heading font-bold text-ink px-5 py-4 w-[40%]">Capability</th>
                <th className="text-left font-semibold text-ink-600 px-5 py-4">{c.competitor}</th>
                <th className="text-left px-5 py-4">
                  <span className="inline-flex items-center gap-1.5 font-heading font-extrabold text-redline-600">Ask Redline</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {c.table.map((row, i) => (
                <tr key={i} className="border-b border-sand-100 last:border-0">
                  <td className="px-5 py-4 font-medium text-ink-800">{row[0]}</td>
                  <td className="px-5 py-4"><Cell value={row[1]} /></td>
                  <td className="px-5 py-4 bg-redline-50/30"><Cell value={row[2]} accent /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink-500 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-redline-500" /> Comparison reflects publicly described capabilities and Ask Redline's resident-communication focus. {c.competitor} is a trademark of its respective owner.</p>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Questions about switching from {c.competitor}</h2>
        <div className="space-y-3">
          {c.faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-sand-200 bg-white p-5" data-testid={`compare-faq-${i}`}>
              <div className="font-heading font-bold text-ink">{f.q}</div>
              <p className="text-ink-700 text-sm mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OTHER COMPARISONS */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400 mb-4">More comparisons</div>
        <div className="flex flex-wrap gap-2.5">
          {COMPETITORS.filter((o) => o.slug !== c.slug).map((o) => (
            <Link key={o.slug} to={`/compare/${o.slug}`} className="rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:border-ink hover:text-ink transition-colors" data-testid={`compare-related-${o.slug}`}>
              {o.competitor} alternative
            </Link>
          ))}
        </div>
      </section>

      <MarketingCTA title={`See why communities pick Ask Redline over ${c.competitor}.`} />
      <MarketingFooter />
    </div>
  );
}
